/**
 * The exploratory workspace's state: availability, sessions, and asking.
 *
 * Shaped like `useCopilot` and different in the ways the two features are
 * different. There are no proposals to hold, because exploration has nowhere
 * to commit anything to. There is no "new chat" that hides earlier turns,
 * because a session is pinned to the state of the run it was opened against
 * and starting a second one is the honest way to start again.
 *
 * Nothing here computes anything. The screen renders what the server sent —
 * the programs, their output and the figures they saved — which is the whole
 * of ADR-0004 as it applies to a surface whose content is arbitrary.
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { getUser } from "../lib/user";

/* Neither on nor off until the server has answered. Rendering the workspace as
   usable and then taking it away is worse than waiting: a scientist starts
   describing an analysis that was never going to run. */
const UNKNOWN = { available: null, kind: null, reason: "" };

const UNREACHABLE = {
  available: false,
  kind: "none",
  reason:
    "The exploratory analysis workspace could not be reached, so nothing can be run here. Your analysis, curation and exports are unaffected.",
};

export function useExploration(runId) {
  const [availability, setAvailability] = useState(UNKNOWN);
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  /* Asked once. What is configured is a property of the installation, not of
     anybody's run, and a failure is read as "not available" rather than
     assumed working — the point of the call is that the workspace must not
     invite an analysis it cannot run. */
  useEffect(() => {
    let live = true;
    api
      .getExplorationAvailability()
      .then((state) => live && setAvailability(state))
      .catch(() => live && setAvailability(UNREACHABLE));
    return () => {
      live = false;
    };
  }, []);

  /* The list is fetched whether or not anything can run. A session may have
     been cited in a decision, and switching the feature off must not take the
     record of it away. */
  useEffect(() => {
    let live = true;
    setSessions([]);
    setCurrent(null);
    api
      .listExplorations(runId)
      .then((rows) => live && setSessions(rows))
      .catch(() => {
        // Not worth interrupting for. The failure shows on the first action.
      });
    return () => {
      live = false;
    };
  }, [runId]);

  const open = useCallback(
    async (sessionId) => {
      setError(null);
      try {
        setCurrent(await api.getExploration(runId, sessionId));
      } catch (e) {
        setError(e);
      }
    },
    [runId]
  );

  const start = useCallback(
    async (title) => {
      if (starting || busy || availability.available !== true) return null;
      setStarting(true);
      setError(null);
      try {
        const opened = await api.startExploration(runId, {
          title: title?.trim() || null,
          createdBy: getUser(),
        });
        setSessions((was) => [...was, opened]);
        setCurrent({ ...opened, messages: [], artifacts: [] });
        return opened;
      } catch (e) {
        setError(e);
        return null;
      } finally {
        setStarting(false);
      }
    },
    [runId, availability, busy, starting]
  );

  const ask = useCallback(
    async (question) => {
      const text = question.trim();
      // The screen greys itself out; this is the second lock, for anything
      // that calls `ask` directly.
      if (!text || busy || !current || availability.available !== true) return;
      setBusy(true);
      setError(null);
      // Shown immediately and replaced by the server's copy when the turn
      // lands. A program takes long enough that a question with no echo reads
      // as a click that did nothing.
      setCurrent((was) => ({
        ...was,
        messages: [...was.messages, { role: "user", content: text, local: true }],
      }));
      try {
        const turn = await api.askExploration(runId, current.id, text, getUser());
        setCurrent((was) => ({
          ...was,
          stale: turn.stale,
          messages: [
            ...was.messages,
            {
              role: "assistant",
              content: turn.answer,
              grounded: turn.grounded,
              executions: turn.executions,
              local: true,
            },
          ],
          artifacts: [...was.artifacts, ...turn.artifacts],
        }));
        setSessions((was) =>
          was.map((s) =>
            s.id === current.id
              ? {
                  ...s,
                  stale: turn.stale,
                  n_messages: s.n_messages + 2,
                  n_artifacts: s.n_artifacts + turn.artifacts.length,
                }
              : s
          )
        );
      } catch (e) {
        setError(e);
        // The optimistic echo goes with it. Leaving the question on screen
        // under a failure reads as a question that was asked and ignored.
        setCurrent((was) => ({
          ...was,
          messages: was.messages.filter((m) => !(m.local && m.role === "user" && m.content === text)),
        }));
      } finally {
        setBusy(false);
      }
    },
    [runId, current, busy, availability]
  );

  return {
    availability,
    sessions,
    current,
    busy,
    starting,
    error,
    start,
    open,
    ask,
    dismissError: () => setError(null),
  };
}
